import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ArrowLeft, MailCheck, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      if (data?.reset_link) setResetLink(data.reset_link);
      setSent(true);
    } catch (err) {
      setSent(true); // never reveal whether the email exists
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(resetLink); toast.success("Link copiat"); } catch { /* noop */ }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="border border-slate-200 p-8" data-testid="forgot-password-page">
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <MailCheck className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-2xl font-black tracking-tight mb-3">{t("auth.forgotTitle")}</h1>
            <p className="text-sm text-slate-500">{t("auth.checkEmail")}</p>

            {resetLink && (
              <div className="mt-6 text-left border-2 border-dashed border-orange-400 bg-orange-50 p-4" data-testid="beta-reset-box">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-black text-orange-700 mb-2">
                  <KeyRound className="w-4 h-4" /> Mod beta · link de resetare
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-3">
                  Trimiterea de emailuri reale nu este încă activată. Folosește linkul de mai jos pentru a-ți reseta parola (valabil 1 oră):
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Link to={new URL(resetLink).pathname + new URL(resetLink).search}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 text-sm font-bold transition-colors"
                    data-testid="beta-reset-link">
                    <KeyRound className="w-4 h-4" /> Resetează parola
                  </Link>
                  <button onClick={copyLink}
                    className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-700 px-4 py-2.5 text-sm font-bold transition-colors"
                    data-testid="beta-reset-copy">
                    <Copy className="w-4 h-4" /> Copiază
                  </button>
                </div>
              </div>
            )}

            <Link to="/autentificare" className="inline-block mt-6 text-orange-600 font-semibold hover:underline text-sm">{t("auth.backToLogin")}</Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tight mb-2">{t("auth.forgotTitle")}</h1>
            <p className="text-sm text-slate-500 mb-8">{t("auth.forgotSubtitle")}</p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("auth.emailLabel")}</label>
                <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-slate-300 h-12 mt-2" data-testid="forgot-email" />
              </div>
              <button disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="forgot-submit">
                {loading ? t("auth.processing") : t("auth.sendLink")}
              </button>
            </form>
            <Link to="/autentificare" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 mt-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> {t("auth.backToLogin")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
